import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  X, Plus, Trash2, Sparkles, ShoppingCart, ShoppingBag, Dumbbell, Pill, Gift,
  ListPlus, Share2, MessageSquare, Mic, Check, Star,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default list "kinds" → icon + label config. Custom user-lists render with
// a generic ListPlus icon and their own user-typed name.
const DEFAULT_TABS = [
  { kind: "groceries",   label: "Groceries",   icon: ShoppingCart },
  { kind: "merch",       label: "Merch",       icon: ShoppingBag },
  { kind: "equipment",   label: "Equipment",   icon: Dumbbell },
  { kind: "supplements", label: "Supplements", icon: Pill },
  { kind: "gifts",       label: "Gifts",       icon: Gift },
];

const LAST_TAB_KEY = "omnimuscle.shopping.lastTab";

export default function ShoppingDrawer({ open, onClose }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [customLists, setCustomLists] = useState([]);
  const [limit, setLimit] = useState(null);
  const [isPremium, setIsPremium] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() =>
    (typeof window !== "undefined" && window.localStorage.getItem(LAST_TAB_KEY)) || "groceries"
  );
  const [quickAdd, setQuickAdd] = useState("");
  const [quickQty, setQuickQty] = useState("");
  const [showAddListPrompt, setShowAddListPrompt] = useState(false);
  const [newListName, setNewListName] = useState("");
  const [recording, setRecording] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  // Premium upsell modal toggle (when free user hits 25 limit or tries premium feature)
  const [upsellMsg, setUpsellMsg] = useState(null);

  const recogRef = useRef(null);

  async function load() {
    if (!user) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/shopping/state`);
      setItems(data?.items || []);
      setCustomLists(data?.custom_lists || []);
      setLimit(data?.limit ?? null);
      setIsPremium(!!data?.is_premium);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load shopping list.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (open && user) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user?.user_id]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(LAST_TAB_KEY, activeTab);
    }
  }, [activeTab]);

  // Smart Suggestions — fetch top-5 most-frequent past adds for the current tab.
  // Re-runs when the user opens the drawer, switches tabs, or after items
  // change (so suggestions stay accurate as the list grows/shrinks).
  useEffect(() => {
    if (!open || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/shopping/suggestions?list_kind=${encodeURIComponent(activeTab)}`
        );
        if (!cancelled) setSuggestions(data?.suggestions || []);
      } catch {
        if (!cancelled) setSuggestions([]);
      }
    })();
    return () => { cancelled = true; };
    // items.length triggers refresh after add/delete/clear
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeTab, items.length, user?.user_id]);

  async function addSuggestion(s) {
    // Optimistically remove from suggestions panel so a double-tap can't dupe
    setSuggestions((prev) => prev.filter((x) => x.name.toLowerCase() !== s.name.toLowerCase()));
    await add(s.name, s.qty);
  }

  const allTabs = useMemo(() => {
    return [
      ...DEFAULT_TABS.map((t) => ({ ...t, custom: false })),
      ...customLists.map((c) => ({ kind: c.list_id, label: c.name, icon: ListPlus, custom: true })),
    ];
  }, [customLists]);

  const itemsForTab = items.filter((i) => i.list_kind === activeTab);
  const totalCount = items.length;
  const remainingCount = limit !== null ? Math.max(0, limit - totalCount) : null;
  const atLimit = limit !== null && totalCount >= limit;

  async function add(name, qty = "") {
    const n = (name || "").trim();
    if (!n) return;
    try {
      const { data } = await axios.post(`${API}/shopping/items`, {
        name: n,
        qty: qty.trim(),
        list_kind: activeTab,
      });
      setItems((prev) => [data.item, ...prev]);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Add failed.";
      if (e?.response?.status === 402) {
        setUpsellMsg(detail);
      } else {
        toast.error(detail);
      }
    }
  }

  async function submitQuickAdd(e) {
    e?.preventDefault?.();
    if (!quickAdd.trim()) return;
    await add(quickAdd, quickQty);
    setQuickAdd("");
    setQuickQty("");
  }

  async function toggle(item) {
    // Optimistic flip
    setItems((prev) =>
      prev.map((i) => (i.item_id === item.item_id ? { ...i, checked: !i.checked } : i))
    );
    try {
      await axios.put(`${API}/shopping/items/${item.item_id}`, { checked: !item.checked });
    } catch (e) {
      // Revert on failure
      setItems((prev) =>
        prev.map((i) => (i.item_id === item.item_id ? { ...i, checked: item.checked } : i))
      );
      toast.error(e?.response?.data?.detail || "Could not update item.");
    }
  }

  async function remove(item) {
    setItems((prev) => prev.filter((i) => i.item_id !== item.item_id));
    try {
      await axios.delete(`${API}/shopping/items/${item.item_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete item.");
      load(); // resync
    }
  }

  async function clearChecked() {
    const checkedCount = itemsForTab.filter((i) => i.checked).length;
    if (checkedCount === 0) return;
    try {
      const { data } = await axios.post(
        `${API}/shopping/items/clear-checked?list_kind=${encodeURIComponent(activeTab)}`
      );
      toast.success(`Cleared ${data?.deleted || checkedCount} checked items.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clear failed.");
    }
  }

  async function createCustomList() {
    const name = newListName.trim();
    if (!name) return;
    try {
      const { data } = await axios.post(`${API}/shopping/lists`, { name });
      setCustomLists((prev) => [...prev, data.list]);
      setActiveTab(data.list.list_id);
      setShowAddListPrompt(false);
      setNewListName("");
      toast.success(`Created list "${name}".`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not create list.";
      if (e?.response?.status === 402) setUpsellMsg(detail);
      else toast.error(detail);
    }
  }

  async function deleteCustomList(listId, label) {
    if (!window.confirm(`Delete the "${label}" list and all its items?`)) return;
    try {
      await axios.delete(`${API}/shopping/lists/${listId}`);
      setCustomLists((prev) => prev.filter((c) => c.list_id !== listId));
      setItems((prev) => prev.filter((i) => i.list_kind !== listId));
      setActiveTab("groceries");
      toast.success(`Deleted "${label}".`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  }

  async function shareCurrentList() {
    if (!isPremium) {
      setUpsellMsg("Share-links are a Premium feature.");
      return;
    }
    try {
      const { data } = await axios.post(
        `${API}/shopping/share?list_kind=${encodeURIComponent(activeTab)}`
      );
      const url = `${window.location.origin}/shopping/shared/${data.token}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Share-link copied to clipboard!");
      } catch {
        // Older browsers — fall back to a prompt
        window.prompt("Copy this share link:", url);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create share-link.");
    }
  }

  // Voice / Speech Recognition quick-add (Chrome/Edge). Falls back silently
  // if the browser doesn't support it.
  function startVoice() {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error("Voice input isn't supported in this browser.");
      return;
    }
    if (recording) {
      try { recogRef.current?.stop(); } catch {}
      return;
    }
    const r = new SR();
    r.lang = "en-US";
    r.interimResults = false;
    r.maxAlternatives = 1;
    r.onresult = (ev) => {
      const transcript = ev.results?.[0]?.[0]?.transcript || "";
      if (transcript) setQuickAdd((prev) => (prev ? `${prev} ${transcript}` : transcript));
    };
    r.onend = () => setRecording(false);
    r.onerror = () => setRecording(false);
    recogRef.current = r;
    setRecording(true);
    try { r.start(); } catch { setRecording(false); }
  }

  if (!user) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className={
          "fixed inset-0 bg-black/60 z-[68] transition-opacity " +
          (open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none")
        }
        onClick={onClose}
        data-testid="shopping-drawer-backdrop"
      />

      {/* Drawer */}
      <aside
        data-testid="shopping-drawer"
        className={
          "fixed top-0 right-0 bottom-0 z-[69] w-full sm:w-[420px] bg-[#0A0A0A] border-l-2 border-[#D4AF37] " +
          "transform transition-transform duration-300 ease-out flex flex-col " +
          (open ? "translate-x-0" : "translate-x-full")
        }
        aria-hidden={!open}
      >
        {/* Header */}
        <div className="p-4 border-b border-zinc-900 flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-1 flex items-center gap-2">
              <ShoppingCart size={12} />
              Shopping List
              {isPremium && (
                <span className="text-[9px] tracking-widest text-emerald-400 normal-case bg-emerald-900/30 border border-emerald-700 px-1.5 py-0.5">
                  <Sparkles size={9} className="inline -mt-0.5" /> PREMIUM
                </span>
              )}
            </div>
            <h2 className="font-display text-2xl text-white leading-tight">
              {allTabs.find((t) => t.kind === activeTab)?.label || "List"}
            </h2>
            {limit !== null && (
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">
                {totalCount}/{limit} items
                {remainingCount === 0 && <span className="text-amber-400 ml-2">· at limit</span>}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            data-testid="shopping-drawer-close"
            className="text-neutral-400 hover:text-white p-1"
            aria-label="Close shopping list"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-3 pt-3 pb-2 border-b border-zinc-900 overflow-x-auto">
          <div className="flex gap-1.5 min-w-max">
            {allTabs.map((t) => {
              const Icon = t.icon;
              const count = items.filter((i) => i.list_kind === t.kind).length;
              const isActive = activeTab === t.kind;
              return (
                <button
                  key={t.kind}
                  onClick={() => setActiveTab(t.kind)}
                  data-testid={`shopping-tab-${t.kind}`}
                  className={
                    "px-2.5 py-1.5 text-[10px] uppercase tracking-widest font-bold border-2 transition-colors flex items-center gap-1.5 whitespace-nowrap " +
                    (isActive
                      ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                      : "border-zinc-800 text-neutral-400 hover:border-zinc-600 hover:text-white")
                  }
                >
                  <Icon size={11} />
                  {t.label}
                  {count > 0 && <span className="text-neutral-500">· {count}</span>}
                </button>
              );
            })}
            {showAddListPrompt ? (
              <form
                onSubmit={(e) => { e.preventDefault(); createCustomList(); }}
                className="flex items-center gap-1"
              >
                <input
                  data-testid="shopping-new-list-input"
                  value={newListName}
                  onChange={(e) => setNewListName(e.target.value)}
                  placeholder="List name…"
                  autoFocus
                  className="bg-[#0F0F0F] border border-[#D4AF37] text-white px-2 py-1 text-xs outline-none w-32"
                  maxLength={60}
                />
                <button
                  type="submit"
                  data-testid="shopping-new-list-create"
                  className="bg-[#D4AF37] text-black p-1.5 hover:bg-[#F1C84B]"
                  aria-label="Create list"
                >
                  <Check size={12} />
                </button>
                <button
                  type="button"
                  onClick={() => { setShowAddListPrompt(false); setNewListName(""); }}
                  className="text-neutral-500 hover:text-white p-1.5"
                  aria-label="Cancel"
                >
                  <X size={12} />
                </button>
              </form>
            ) : (
              <button
                onClick={() => {
                  if (!isPremium) {
                    setUpsellMsg("Custom lists are a Premium feature.");
                    return;
                  }
                  setShowAddListPrompt(true);
                }}
                data-testid="shopping-add-custom-list"
                className="px-2.5 py-1.5 text-[10px] uppercase tracking-widest font-bold border-2 border-dashed border-zinc-700 text-neutral-500 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors flex items-center gap-1 whitespace-nowrap"
                title={isPremium ? "Create a custom list" : "Premium: create your own lists"}
              >
                <Plus size={11} /> List
              </button>
            )}
          </div>
        </div>

        {/* Quick-add */}
        <div className="p-3 border-b border-zinc-900 bg-[#0F0F0F]">
          <form onSubmit={submitQuickAdd} className="flex gap-1.5">
            <input
              data-testid="shopping-quick-add"
              value={quickAdd}
              onChange={(e) => setQuickAdd(e.target.value)}
              placeholder={atLimit ? "Free limit reached — upgrade for unlimited" : "Add an item…"}
              disabled={atLimit}
              maxLength={120}
              className="flex-1 bg-[#0A0A0A] border border-zinc-800 focus:border-[#D4AF37] text-white px-3 py-2 text-sm outline-none transition-colors disabled:opacity-50"
            />
            <input
              data-testid="shopping-quick-add-qty"
              value={quickQty}
              onChange={(e) => setQuickQty(e.target.value)}
              placeholder="Qty"
              disabled={atLimit}
              maxLength={40}
              className="w-16 bg-[#0A0A0A] border border-zinc-800 focus:border-[#D4AF37] text-white px-2 py-2 text-sm outline-none transition-colors disabled:opacity-50"
            />
            <button
              type="button"
              onClick={startVoice}
              disabled={atLimit}
              data-testid="shopping-voice-btn"
              title="Voice add (Chrome/Edge)"
              className={
                "px-2.5 border-2 transition-colors disabled:opacity-50 " +
                (recording
                  ? "border-red-500 bg-red-900/30 text-red-400 animate-pulse"
                  : "border-zinc-800 text-neutral-500 hover:border-[#D4AF37] hover:text-[#D4AF37]")
              }
            >
              <Mic size={14} />
            </button>
            <button
              type="submit"
              disabled={atLimit || !quickAdd.trim()}
              data-testid="shopping-quick-add-submit"
              className="bg-[#D4AF37] hover:bg-[#F1C84B] text-black px-3 py-2 text-xs font-bold uppercase tracking-widest transition-colors disabled:opacity-50"
            >
              <Plus size={14} />
            </button>
          </form>
        </div>

        {/* Body — items list */}
        <div className="flex-1 overflow-y-auto" data-testid="shopping-items-list">
          {/* Smart Suggestions — top-5 most-frequent past adds for this tab */}
          {!loading && suggestions.length > 0 && !atLimit && (
            <div className="px-3 pt-3 pb-2 border-b border-zinc-900" data-testid="shopping-suggestions">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-2 flex items-center gap-1.5">
                <Star size={11} /> Smart Suggestions
              </div>
              <div className="flex flex-wrap gap-1.5">
                {suggestions.map((s) => (
                  <button
                    key={s.name}
                    type="button"
                    onClick={() => addSuggestion(s)}
                    data-testid={`shopping-suggestion-${s.name.toLowerCase().replace(/\s+/g, "-")}`}
                    title={`Used ${s.count}× — tap to add`}
                    className="text-[11px] bg-[#0F0F0F] border border-zinc-700 hover:border-[#D4AF37] hover:text-[#D4AF37] text-neutral-300 px-2 py-1 transition-colors flex items-center gap-1 group"
                  >
                    <Plus size={10} className="text-[#D4AF37]" />
                    <span className="truncate max-w-[140px]">{s.name}</span>
                    {s.qty && <span className="text-neutral-500">× {s.qty}</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {loading ? (
            <div className="text-center text-neutral-500 py-12 text-sm">Loading…</div>
          ) : itemsForTab.length === 0 ? (
            <div className="text-center text-neutral-500 py-16 px-6 text-sm">
              Nothing here yet. Add your first item above. ↑
            </div>
          ) : (
            <ul className="divide-y divide-zinc-900">
              {itemsForTab.map((item) => (
                <li
                  key={item.item_id}
                  data-testid={`shopping-item-${item.item_id}`}
                  className={
                    "p-3 flex items-center gap-3 transition-colors hover:bg-[#0F0F0F] " +
                    (item.checked ? "opacity-50" : "")
                  }
                >
                  <button
                    type="button"
                    onClick={() => toggle(item)}
                    data-testid={`shopping-item-toggle-${item.item_id}`}
                    aria-label={item.checked ? "Uncheck" : "Check"}
                    className={
                      "flex-shrink-0 w-6 h-6 border-2 flex items-center justify-center transition-colors " +
                      (item.checked
                        ? "border-[#D4AF37] bg-[#D4AF37] text-black"
                        : "border-zinc-700 text-transparent hover:border-[#D4AF37]")
                    }
                  >
                    <Check size={14} />
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className={
                      "text-sm text-white leading-tight truncate " +
                      (item.checked ? "line-through" : "")
                    }>
                      {item.name}
                      {item.qty && (
                        <span className="text-neutral-500 ml-2 text-xs">× {item.qty}</span>
                      )}
                    </div>
                    {item.note && (
                      <div className="text-[10px] text-neutral-500 truncate mt-0.5">{item.note}</div>
                    )}
                    {item.source && item.source !== "manual" && (
                      <div className="text-[9px] uppercase tracking-widest text-[#D4AF37]/70 mt-0.5">
                        from {item.source.replace("_", " ")}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => remove(item)}
                    data-testid={`shopping-item-delete-${item.item_id}`}
                    aria-label="Delete item"
                    className="flex-shrink-0 text-neutral-600 hover:text-red-400 p-1"
                  >
                    <Trash2 size={14} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer actions */}
        <div className="p-3 border-t border-zinc-900 bg-[#0F0F0F] flex flex-wrap gap-2">
          <button
            type="button"
            onClick={clearChecked}
            disabled={itemsForTab.filter((i) => i.checked).length === 0}
            data-testid="shopping-clear-checked"
            className="text-[10px] uppercase tracking-widest font-bold border border-zinc-800 hover:border-[#D4AF37] hover:text-[#D4AF37] text-neutral-400 px-2.5 py-1.5 transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1"
          >
            <Trash2 size={11} /> Clear Checked
          </button>
          <button
            type="button"
            onClick={shareCurrentList}
            data-testid="shopping-share"
            className="text-[10px] uppercase tracking-widest font-bold border border-zinc-800 hover:border-[#D4AF37] hover:text-[#D4AF37] text-neutral-400 px-2.5 py-1.5 transition-colors flex items-center gap-1"
            title={isPremium ? "Create a shareable read-only link" : "Premium: share with household"}
          >
            <Share2 size={11} /> Share {!isPremium && <Sparkles size={10} className="text-[#D4AF37]" />}
          </button>
          <button
            type="button"
            onClick={() => setUpsellMsg("SMS-to-phone is coming soon (Premium). We'll text your list when you head out.")}
            data-testid="shopping-sms"
            className="text-[10px] uppercase tracking-widest font-bold border border-zinc-800 hover:border-[#D4AF37] hover:text-[#D4AF37] text-neutral-400 px-2.5 py-1.5 transition-colors flex items-center gap-1"
          >
            <MessageSquare size={11} /> SMS <Sparkles size={10} className="text-[#D4AF37]" />
          </button>
          {customLists.find((c) => c.list_id === activeTab) && (
            <button
              type="button"
              onClick={() => {
                const c = customLists.find((x) => x.list_id === activeTab);
                deleteCustomList(c.list_id, c.name);
              }}
              data-testid="shopping-delete-current-list"
              className="text-[10px] uppercase tracking-widest font-bold border border-red-900 text-red-400 hover:bg-red-700 hover:text-white px-2.5 py-1.5 transition-colors flex items-center gap-1 ml-auto"
            >
              <Trash2 size={11} /> Delete List
            </button>
          )}
        </div>
      </aside>

      {/* Premium upsell modal */}
      {upsellMsg && (
        <div
          className="fixed inset-0 bg-black/85 z-[80] flex items-center justify-center p-4"
          onClick={() => setUpsellMsg(null)}
        >
          <div
            className="bg-[#0F0F0F] border-2 border-[#D4AF37] max-w-sm w-full p-6 text-center"
            onClick={(e) => e.stopPropagation()}
            data-testid="shopping-upsell-modal"
          >
            <Sparkles size={32} className="text-[#D4AF37] mx-auto mb-3" />
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-1">
              Premium Feature
            </div>
            <p className="text-sm text-neutral-300 mb-5 leading-relaxed">{upsellMsg}</p>
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => setUpsellMsg(null)}
                data-testid="shopping-upsell-close"
                className="border border-zinc-700 hover:border-white text-white px-4 py-2 text-xs uppercase tracking-widest font-bold"
              >
                Maybe later
              </button>
              <a
                href="/plans"
                data-testid="shopping-upsell-upgrade"
                className="bg-[#D4AF37] hover:bg-[#F1C84B] text-black px-4 py-2 text-xs uppercase tracking-widest font-bold"
              >
                See Plans →
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
